import React from 'react';
import { MetricCard } from './componentUtils';
import {
  Assignment,
  Grade,
  EmojiEvents,
  SpeedOutlined,
  Leaderboard,
  TimerOutlined,
  RocketLaunch,
  Star,
  TrendingUpOutlined,
  WorkspacePremium
} from '@mui/icons-material';
import { Grid, Rating } from '@mui/material';

/**
 * Creates a collection of metric cards for the dashboard
 * @param {Object} data - The dashboard data containing metrics
 * @param {string} mode - The current theme mode ('light' or 'dark')
 * @returns {Object} - An object containing different sets of metric cards
 */
export const createMetricCards = (data, mode) => {
  const { overview = {} } = data || {};

  // Primary metrics (top row)
  const primaryMetrics = [
    {
      id: 'totalTests',
      title: 'Total Tests Taken',
      value: overview?.totalTests || 0,
      icon: <Assignment />,
      color: 'primary.main',
      variant: 'default',
      trend: {
        icon: <TrendingUpOutlined color="success" fontSize="small" />,
        text: `+${overview?.testsPerDay || 0} per day`,
        color: 'success.main'
      }
    },
    {
      id: 'averageScore',
      title: 'Average Score',
      value: `${(overview?.averageScore || 0).toFixed(1)}%`,
      icon: <Grade />,
      color: 'success.main',
      variant: 'success',
      progress: {
        value: overview?.averageScore || 0,
        color: 'success'
      },
      target: '85%'
    },
    {
      id: 'bestPerformance',
      title: 'Best Performance',
      value: `${(overview?.bestScore || 0).toFixed(1)}%`,
      icon: <EmojiEvents />,
      color: 'warning.main',
      variant: 'warning',
      trend: {
        icon: <WorkspacePremium color="warning" fontSize="small" />,
        text: 'Personal Best',
        color: 'warning.main'
      }
    },
    {
      id: 'accuracySpeed',
      title: 'Accuracy & Speed',
      value: `${(overview?.averageAccuracy || 0).toFixed(1)}%`,
      subtitle: `Speed: ${(overview?.averageSpeed || 0).toFixed(1)} q/min`,
      icon: <SpeedOutlined />,
      color: 'info.main',
      variant: 'info'
    }
  ];

  // Secondary metrics (second row)
  const secondaryMetrics = [
    {
      id: 'allIndiaRank',
      title: 'All India Rank',
      value: `#${overview?.allIndiaRank || 'N/A'}`,
      subtitle: `Out of ${overview?.totalTestTakers || 0} students`,
      icon: <Leaderboard />,
      color: 'secondary.main',
      variant: 'secondary'
    },
    {
      id: 'timeEfficiency',
      title: 'Time Efficiency',
      value: `${(overview?.averageTimeEfficiency || 0).toFixed(1)}%`,
      subtitle: `Avg: ${(overview?.averageTimePerQuestion || 0).toFixed(1)} min/q`,
      icon: <TimerOutlined />,
      color: 'error.main',
      variant: 'error'
    },
    {
      id: 'studyStreak',
      title: 'Study Streak',
      value: `${overview?.activeDays || 0} days`,
      subtitle: `${overview?.totalDays || 0} total days active`,
      icon: <RocketLaunch />,
      color: 'success.main',
      variant: 'primary'
    },
    {
      id: 'performanceLevel',
      title: 'Performance Level',
      value: getPerformanceLevel(overview?.averageScore || 0),
      icon: <Star />,
      color: 'primary.main',
      variant: 'info',
      customContent: (
        <Rating 
          value={getPerformanceStars(overview?.averageScore || 0)} 
          readOnly 
          size="small"
          sx={{ color: 'gold' }}
        />
      )
    }
  ];

  return {
    primaryMetrics,
    secondaryMetrics,
    allMetrics: [...primaryMetrics, ...secondaryMetrics]
  };
};

/**
 * Renders a collection of metric cards
 * @param {Array} metrics - Array of metric card configurations
 * @param {string} mode - The current theme mode ('light' or 'dark')
 * @returns {Array} - Array of rendered metric card components
 */
export const renderMetricCards = (metrics, mode) => {
  return metrics.map((metric) => (
    <Grid item xs={6} sm={6} md={3} key={metric.id} sx={{ minWidth: 0 }}>
      <MetricCard
        title={metric.title}
        value={metric.value}
        subtitle={metric.subtitle}
        icon={metric.icon}
        color={metric.color}
        variant={metric.variant}
        mode={mode}
        trend={metric.trend}
        progress={metric.progress}
        target={metric.target}
        customContent={metric.customContent}
      />
    </Grid>
  ));
};

// Helper functions for performance metrics
const getPerformanceLevel = (score) => {
  if (score >= 90) return 'Expert';
  if (score >= 80) return 'Advanced';
  if (score >= 70) return 'Intermediate';
  if (score >= 60) return 'Beginner';
  return 'Novice';
};

const getPerformanceStars = (score) => {
  if (score >= 90) return 5;
  if (score >= 80) return 4;
  if (score >= 70) return 3;
  if (score >= 60) return 2;
  return 1;
};